import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSimulation, resetDashboardDetails, resetTimelineState } from '../../redux/slices/timeline';
import { resetRoadmap } from '../../redux/slices/roadmap';
import { useRoadmapService } from '../../services';
import { SimulationDiffData } from './types';
import { animate, stagger } from 'motion';
import { 
  HiOutlinePlus, 
  HiOutlineTrash, 
  HiOutlineLink,
  HiOutlineChevronDown,
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineTag
} from 'react-icons/hi';

interface SimulationDiffDisplayProps {
  diffData: SimulationDiffData;
}

interface TaskDiffItemProps {
  task: any;
  isExpanded: boolean;
  onToggle: () => void;
  onLocateTask: (taskId: string) => void;
  hasViewedProject: boolean;
  isDescriptionExpanded: boolean;
  onToggleDescription: () => void;
  isSharedChat: boolean;
}

const TaskDiffItem: React.FC<TaskDiffItemProps> = ({ task, isExpanded, onToggle, onLocateTask, hasViewedProject, isDescriptionExpanded, onToggleDescription, isSharedChat }) => {
  const { task_id, operation, data } = task;
  
  // Get change indicators for collapsed state
  const getChangeIndicators = () => {
    if (operation === 'add') return [];
    if (operation === 'delete') return [];
    
    const indicators = [] as any[];
    if (data.updated_fields) {
      const fieldLabels: { [key: string]: { label: string } } = {
        'dependency': { label: 'Dependencies' },
        'start_date': { label: 'Start Date' },
        'skill_required': { label: 'Skill' },
        'task_title': { label: 'Title' },
        'description': { label: 'Description' },
        'duration': { label: 'Duration' }
      };
      
      for (const field of data.updated_fields) {
        const fieldInfo = fieldLabels[field];
        if (fieldInfo) {
          indicators.push(
            <span 
              key={field} 
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 mr-1`}
            >
              {fieldInfo.label}
            </span>
          );
        }
      }
    }
    return indicators;
  };
  
  // Get task title from different data structures
  const getTaskTitle = () => {
    if (data.task_title) return data.task_title;
    if (data.task?.task_title) return data.task.task_title;
    if (data.field_changes?.task_title?.after) return data.field_changes.task_title.after;
    return `Task ${task_id}`;
  };
  
  const taskTitle = getTaskTitle();

  const getTargetTaskId = () => {
    if (task.task_id) return String(task.task_id);
    if (data?.task?.id) return String(data.task.id);
    if (data?.task?.item_id) return String(data.task.item_id);
    if (data?.field_changes?.id?.after) return String(data.field_changes.id.after);
    if (data?.field_changes?.task_id?.after) return String(data.field_changes.task_id.after);
    return String(task_id);
  };
  
  const renderTaskDetails = () => {
    if (!isExpanded) return null;

    const getDescriptionData = (description: string) => {
      const descText = String(description).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      const descPreview = descText.length > 120 ? `${descText.slice(0, 120).trimEnd()}…` : descText;
      const isDescLong = descText.length > 120;
      return { descText, descPreview, isDescLong };
    };

    switch (data.type) {
      case 'added': {
        const addedDesc = data.task.description ? getDescriptionData(data.task.description) : null;
        
        return (
          <div className="px-3 pb-3">
            <div className="bg-green-50/50 dark:bg-green-900/10 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {task_id}
                </span>
                {/* <span className="text-xs text-gray-500 dark:text-gray-400">New task</span> */}
              </div>
              
              {addedDesc && (
                <div className="bg-white/60 dark:bg-gray-900/20 rounded p-2">
                  <div className={`text-xs text-green-600 dark:text-green-400 ${isDescriptionExpanded && addedDesc.isDescLong ? 'max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                    <span>{isDescriptionExpanded || !addedDesc.isDescLong ? addedDesc.descText : addedDesc.descPreview}</span>
                  </div>
                  {addedDesc.isDescLong && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDescription();
                      }}
                      className="mt-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline transition-colors duration-150 inline-flex items-center gap-0.5"
                    >
                      {isDescriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                  <HiOutlineClock className="w-3 h-3 inline mr-1" />
                  {data.task.duration}d
                </span>
                {data.task.start_date && (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                    <HiOutlineTag className="w-3 h-3 inline mr-1" />
                    {data.task.start_date}
                  </span>
                )}
                {data.task.skill_required && (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                    <HiOutlineUser className="w-3 h-3 inline mr-1" />
                    {data.task.skill_required}
                  </span>
                )}
                {data.task.dependencies && Array.isArray(data.task.dependencies) && data.task.dependencies.length > 0 && (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                    <HiOutlineLink className="w-3 h-3 inline mr-1" />
                    {data.task.dependencies.join(', ')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'deleted': {
        const deletedDesc = data.task.description ? getDescriptionData(data.task.description) : null;
        
        return (
          <div className="px-3 pb-3">
            <div className="bg-red-50/50 dark:bg-red-900/10 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {task_id}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Deleted task</span>
              </div>
              
              {deletedDesc && (
                <div className="bg-white/60 dark:bg-gray-900/20 rounded p-2">
                  <div className={`text-xs text-red-600 dark:text-red-400 ${isDescriptionExpanded && deletedDesc.isDescLong ? 'max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                    <span className="line-through">{isDescriptionExpanded || !deletedDesc.isDescLong ? deletedDesc.descText : deletedDesc.descPreview}</span>
                  </div>
                  {deletedDesc.isDescLong && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDescription();
                      }}
                      className="mt-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline transition-colors duration-150 inline-flex items-center gap-0.5"
                    >
                      {isDescriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-full">
                  <HiOutlineClock className="w-3 h-3 inline mr-1" />
                  {data.task.duration}d
                </span>
                {data.task.start_date && (
                  <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-full">
                    <HiOutlineTag className="w-3 h-3 inline mr-1" />
                    {data.task.start_date}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'updated':
        return (
          <div className="px-3 pb-3">
            <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {task_id}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {data.updated_fields ? `${data.updated_fields.length} field${data.updated_fields.length !== 1 ? 's' : ''} updated` : 'Updated task'}
                </span>
              </div>
              
              {/* Change indicators */}
              <div className="flex flex-wrap gap-1 mb-2">
                {getChangeIndicators()}
              </div>
              
              {data.field_changes && Object.keys(data.field_changes).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(data.field_changes).map(([field, change]: [string, any]) => {
                    if (!change.has_changed) return null;
                    
                    const fieldLabel = field === 'dependency' ? 'Dependencies' : field.replace('_', ' ');
                    
                    if (field === 'description') {
                      const processDesc = (value: any) => {
                        if (value === null || value === undefined || value === '') return null;
                        const descText = String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                        const descPreview = descText.length > 120 ? `${descText.slice(0, 120).trimEnd()}…` : descText;
                        const isDescLong = descText.length > 120;
                        return { descText, descPreview, isDescLong };
                      };

                      const beforeDesc = processDesc(change.before);
                      const afterDesc = processDesc(change.after);

                      return (
                        <div key={field} className="text-xs">
                          <span className="font-medium text-blue-700 dark:text-blue-300 capitalize text-xs mb-1 block">
                            {fieldLabel}:
                          </span>
                          <div className="space-y-2">
                            {beforeDesc && (
                              <div className="bg-red-100 dark:bg-red-900/30 rounded-md p-2">
                                <div className={`text-xs text-red-700 dark:text-red-300 ${isDescriptionExpanded && beforeDesc.isDescLong ? 'max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                                  <span >{isDescriptionExpanded || !beforeDesc.isDescLong ? beforeDesc.descText : beforeDesc.descPreview}</span>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center justify-center">
                              <span className="text-gray-400 text-lg">↓</span>
                            </div>
                            {afterDesc && (
                              <div className="bg-green-100 dark:bg-green-900/30 rounded-md p-2">
                                <div className={`text-xs text-green-700 dark:text-green-300 ${isDescriptionExpanded && afterDesc.isDescLong ? 'max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                                  <span>{isDescriptionExpanded || !afterDesc.isDescLong ? afterDesc.descText : afterDesc.descPreview}</span>
                                </div>
                              </div>
                            )}
                            {((beforeDesc?.isDescLong) || (afterDesc?.isDescLong)) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleDescription();
                                }}
                                className="mt-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline transition-colors duration-150 inline-flex items-center gap-0.5"
                              >
                                {isDescriptionExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    const formatValue = (value: any) => {
                      if (Array.isArray(value)) {
                        return value.length > 0 ? value.join(', ') : 'None';
                      }
                      if (value === null || value === undefined || value === '') {
                        return 'None';
                      }
                      return String(value).replace(/<[^>]*>/g, '').slice(0, 60);
                    };
                    
                    return (
                      <div key={field} className="text-xs">
                        <span className="font-medium text-blue-700 dark:text-blue-300 capitalize text-xs mb-1 block">
                          {fieldLabel}:
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-md text-xs">
                            {formatValue(change.before)}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-md text-xs">
                            {formatValue(change.after)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'dependency_update':
        return (
          <div className="px-3 pb-3">
            <div className="bg-purple-50/50 dark:bg-purple-900/10 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {task_id}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Dependencies modified</span>
              </div>
              
              {data.dependencies && (
                <div className="space-y-2">
                  {data.dependencies.added_dependencies?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <HiOutlinePlus className="w-3 h-3 text-green-600" />
                      <div className="flex flex-wrap gap-1">
                        {data.dependencies.added_dependencies.map((dep: string) => (
                          <span key={dep} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-md text-xs">
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.dependencies.removed_dependencies?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <HiOutlineTrash className="w-3 h-3 text-red-600" />
                      <div className="flex flex-wrap gap-1">
                        {data.dependencies.removed_dependencies.map((dep: string) => (
                          <span key={dep} className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded-md text-xs line-through">
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="px-3 pb-3">
            <div className="bg-gray-50 dark:bg-gray-900/10 rounded-lg p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Unknown operation: {data.type}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`border rounded-lg border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-secondary-dark transition-all duration-200 hover:shadow-sm overflow-hidden cp-sim-diff-card`}>
      <div 
        className="flex items-center justify-between p-3 cursor-pointer group"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
            {(operation === 'add') && (
                <div className="px-2 py-1 text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-green-400 rounded-lg first-letter:uppercase">
                  New
                </div>
              )}
              {(operation === 'update') && (
                <div className="px-2 py-1 text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-indigo-400 rounded-lg first-letter:uppercase">
                  Updated
                </div>
              )}
              {(operation === 'delete') && (
                <div className="px-2 py-1 text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-red-400 rounded-lg first-letter:uppercase">
                  Deleted
                </div>
              )}
              {(operation === 'dependency_update') && (
                <div className="px-2 py-1 text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-purple-400 rounded-lg first-letter:uppercase">
                  Dependencies
                </div>
              )}
              {taskTitle && taskTitle !== `Task ${task_id}` ? (
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={taskTitle}>
                  {taskTitle}
                </span>
              ) : (
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {task_id}
                </span>
              )}
              

            </div>
            
            {/* Change indicators for collapsed state */}
            {!isExpanded && (operation === 'update' || operation === 'dependency_update') && (
              <div className="flex flex-wrap gap-1 mt-1">
                {getChangeIndicators()}
              </div>
            )}
            
            {/* Task metadata badges for collapsed state */}
            {!isExpanded && (
              <div className="flex flex-wrap gap-1 mt-1">
                {/* Duration badge */}
                {(operation === 'add' && data.task?.duration) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineClock className="w-3 h-3 inline mr-1" />
                    {data.task.duration}d
                  </span>
                )}
                {(operation === 'delete' && data.task?.duration) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineClock className="w-3 h-3 inline mr-1" />
                    {data.task.duration}d
                  </span>
                )}
                {/* {(operation === 'update' && data.field_changes?.duration?.after) && (
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
                    <HiOutlineClock className="w-3 h-3 inline mr-1" />
                    {data.field_changes.duration.after}d
                  </span>
                )} */}

                {/* Start Date badge */}
                {(operation === 'add' && data.task?.start_date) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineTag className="w-3 h-3 inline mr-1" />
                    {data.task.start_date}
                  </span>
                )}
                {(operation === 'delete' && data.task?.start_date) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineTag className="w-3 h-3 inline mr-1" />
                    {data.task.start_date}
                  </span>
                )}
                {(operation === 'update' && data.field_changes?.start_date?.after) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineTag className="w-3 h-3 inline mr-1" />
                    {data.field_changes.start_date.after}
                  </span>
                )}

                {/* Skill Required badge */}
                {(operation === 'add' && data.task?.skill_required) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineUser className="w-3 h-3 inline mr-1" />
                    {data.task.skill_required}
                  </span>
                )}
                {(operation === 'delete' && data.task?.skill_required) && (
                  <span className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 px-2 py-1 rounded-full text-xs">
                    <HiOutlineUser className="w-3 h-3 inline mr-1" />
                    {data.task.skill_required}
                  </span>
                )}
                {/* {(operation === 'update' && data.field_changes?.skill_required?.after) && (
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
                    <HiOutlineUser className="w-3 h-3 inline mr-1" />
                    {data.field_changes.skill_required.after}
                  </span>
                )} */}
              </div>
            )}
            
            {/* Change count for collapsed state */}
            {/* {!isExpanded && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {operation === 'add' && 'New task added'}
                {operation === 'delete' && 'Task removed'}
                {operation === 'update' && data.updated_fields && (
                  `${data.updated_fields.length} field${data.updated_fields.length !== 1 ? 's' : ''} updated`
                )}
                {operation === 'dependency_update' && 'Dependencies modified'}
              </div>
            )} */}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
         
          {!isExpanded && (operation === 'update' || operation === 'dependency_update') && data.updated_fields && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-blue-500 text-white rounded-full">
              {data.updated_fields.length}
            </span>
          )}
          {hasViewedProject && operation !== 'delete' && !isSharedChat && (
            <button
             onClick={(e) => { e.stopPropagation(); onLocateTask(getTargetTaskId()); }}
             className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
             title="View this task in timeline"
           >
             View
           </button>
          )}
          <div className="transition-transform duration-200 group-hover:scale-110">
            <div className={`transition-transform duration-300 ease-in-out ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
              <HiOutlineChevronDown className="w-4 h-4 text-gray-500" />
            </div>
          </div>
        </div>
      </div>
      
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className={isExpanded ? 'cp-sim-diff-expanded' : ''}>
          {renderTaskDetails()}
        </div>
      </div>
    </div>
  );
};

const SimulationDiffDisplay: React.FC<SimulationDiffDisplayProps> = ({ diffData }) => {
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [isOverallExpanded, setIsOverallExpanded] = useState(false); 
  const [hasViewedProject, setHasViewedProject] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { simulation: simActive } = useSelector((state: any) => state.timeline);
  const isSharedChat = useSelector((state: any) => state.chatMessages.isSharedChat);
  const { simulation_diff, workitem_name, workitem_id } = diffData;
  const { fetchData } = useRoadmapService(workitem_id || "");

  useEffect(() => {
    if (isOverallExpanded) {
      try {
        (animate as any)(
          '.cp-sim-diff-card',
          { opacity: [0, 1], transform: ['translateY(6px)', 'translateY(0px)'] },
          { duration: 0.25, delay: (stagger as any)(0.04), easing: 'ease-out' }
        );
      } catch (e) {
        // non-fatal; motion is optional
      }
    } else {
      setExpandedTasks(new Set());
      setExpandedDescriptions({});
    }
  }, [isOverallExpanded, diffData?.simulation_diff?.tasks?.length]);

  // Animation for individual task expansion
  useEffect(() => {
    try {
      const expandedElements = document.querySelectorAll('.cp-sim-diff-expanded');
      if (expandedElements.length > 0) {
        (animate as any)(
          expandedElements,
          { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0px)'] },
          { duration: 0.25, easing: 'ease-out' }
        );
      }
    } catch (e) {
      // non-fatal; motion is optional
    }
  }, [expandedTasks]);

  // Animation for main container expansion
  useEffect(() => {
    if (isOverallExpanded) {
      try {
        const mainContent = document.querySelector('.cp-sim-diff-main-content');
        if (mainContent) {
          (animate as any)(
            mainContent,
            { opacity: [0, 1] },
            { duration: 0.3, easing: 'ease-out' }
          );
        }
      } catch (e) {
        // non-fatal; motion is optional
      }
    }
  }, [isOverallExpanded]);

  const toggleTask = (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    const isCurrentlyExpanded = newExpanded.has(taskId);
    
    if (isCurrentlyExpanded) {
      newExpanded.delete(taskId);
      setExpandedDescriptions(prev => ({ ...prev, [taskId]: false }));
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const toggleDescription = (taskId: string) => {
    setExpandedDescriptions(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const { summary, tasks } = simulation_diff;

  const handleViewProject = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!workitem_id) return;
    setIsOverallExpanded(true);
    // Mark that project view has been activated
    setHasViewedProject(true);
    
    const targetPath = `/projects/${workitem_id}`;
    const isOnProject = location.pathname === targetPath;
    let targetSimFlag = simActive;
    if (!simActive) {
      dispatch(setSimulation(true));
      targetSimFlag = true;
    }
    window.dispatchEvent(new CustomEvent('cadencepro:chat-shrink'));    
    if (!isOnProject) {
      dispatch(resetTimelineState());
      dispatch(setSimulation(true));
      navigate(targetPath);
      return;
    }
    
    try {
      // dispatch(resetDashboardDetails());
      dispatch(resetRoadmap());
      await fetchData(workitem_id, targetSimFlag, {});
    } catch (error) {
      console.error('Error refreshing project data:', error);
    }
  };

  if (!tasks || tasks.length === 0) {
    return null;
  }

  return (
    <div className="my-4 border border-gray-200 dark:border-gray-800 rounded-xl bg-white dark:bg-tertiary-dark shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 sm:p-4 cursor-pointer bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all duration-200"
        onClick={() => setIsOverallExpanded(!isOverallExpanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <HiOutlineLink className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
              Simulation Results
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
              {workitem_name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          
            {/* <button
              onClick={handleViewProject}
              className="hidden sm:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              title="Open project in simulation to view changes"
            >
              View in Project
            </button> */}
          
          {/* Compact summary */}
          <div className="flex items-center gap-1 text-xs">
            {summary.operations.added > 0 && (
              <span className="bg-green-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                +{summary.operations.added}
              </span>
            )}
            {summary.operations.updated > 0 && (
              <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                ~{summary.operations.updated}
              </span>
            )}
            {summary.operations.deleted > 0 && (
              <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                -{summary.operations.deleted}
              </span>
            )}
            {summary.operations.dependency_updates > 0 && (
              <span className="bg-purple-500 text-white px-1.5 py-0.5 rounded-full font-medium">
                ↗{summary.operations.dependency_updates}
              </span>
            )}
          </div>
          
          <div className="transition-transform duration-200">
            <div className={`transition-transform duration-300 ease-in-out ${isOverallExpanded ? 'rotate-0' : '-rotate-90'}`}>
              <HiOutlineChevronDown className="w-4 h-4 text-gray-500" />
            </div>
          </div>
        </div>
      </div>

   
        {!isSharedChat && (
          <div className="border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-tertiary-dark">
            <div className={`px-4 pt-3  flex items-center justify-between ${isOverallExpanded ? '' : 'pb-3'}`}>
              <span className="text-xs text-gray-600 dark:text-gray-400">View these changes in the project</span>
              <button
                onClick={handleViewProject}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                View
              </button>
            </div>
          </div>
        )}
     

      {/* Content */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          isOverallExpanded ? 'max-h-96 opacity-100 overflow-hidden' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className={`pl-2 pr-1 py-2  ${isOverallExpanded ? 'cp-sim-diff-main-content' : ''}`}>
          {/* Task list */}
          <div className="max-h-80 overflow-y-auto  space-y-2 ">
            {tasks.map((task, index) => (
              <TaskDiffItem
                key={`${task.task_id}-${index}`}
                task={task}
                isExpanded={expandedTasks.has(task.task_id)}
                onToggle={() => toggleTask(task.task_id)}
                hasViewedProject={hasViewedProject}
                isDescriptionExpanded={expandedDescriptions[task.task_id] || false}
                onToggleDescription={() => toggleDescription(task.task_id)}
                isSharedChat={isSharedChat}
                onLocateTask={(targetTaskId: string) => {
                  const targetPath = `/projects/${workitem_id}`;
                  const isOnProject = location.pathname === targetPath;
                  const uniqueTaskId = `task__${workitem_id}__${targetTaskId}`;                  
                  if (!simActive) {
                    dispatch(setSimulation(true));
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('cadencepro:locate-task', { 
                        detail: { 
                          projectId: workitem_id, 
                          taskId: targetTaskId,
                          uniqueTaskId: uniqueTaskId 
                        } 
                      }));
                    }, 300);
                  }
                  window.dispatchEvent(new CustomEvent('cadencepro:chat-shrink'));
                  if (!isOnProject) {
                    dispatch(resetTimelineState());
                    dispatch(setSimulation(true));
                    navigate(targetPath);
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('cadencepro:locate-task', { 
                        detail: { 
                          projectId: workitem_id, 
                          taskId: targetTaskId,
                          uniqueTaskId: uniqueTaskId 
                        } 
                      }));
                    }, 300);
                  } else {
                    window.dispatchEvent(new CustomEvent('cadencepro:locate-task', { 
                      detail: { 
                        projectId: workitem_id, 
                        taskId: targetTaskId,
                        uniqueTaskId: uniqueTaskId 
                      } 
                    }));
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationDiffDisplay;
